/**
 * Office Property Type Handler
 * 
 * Specific implementation for Office properties
 * Commercial office spaces for business operations
 */

import { PropertyType } from '../../../shared/definitions/property';
import { CommercialPropertyHandler } from '../base/commercial-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class OfficeHandler extends CommercialPropertyHandler {
  readonly propertyType = PropertyType.Office;
  readonly displayName = 'Office';
  readonly pluralName = 'Offices';
  readonly icon = 'business';
  readonly description = 'Commercial office space for business operations';

  getRoomTypes(): RoomTypeConfig[] {
    return [
      {
        typeKey: 'office_room',
        label: 'properties.roomTypes.office_room.label',
        pluralLabel: 'properties.roomTypes.office_room.plural',
        countField: 'officeRooms',
        icon: 'meeting_room',
        defaultCount: 0,
        description: 'properties.roomTypes.office_room.description',
        formFields: [
          {
            name: 'name',
            label: 'properties.fields.officeName',
            type: 'text',
            placeholder: 'properties.fieldPlaceholders.officeName'
          },
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            required: true,
            min: 5,
            max: 200
          },
          {
            name: 'capacity',
            label: 'properties.fields.capacity',
            type: 'number',
            min: 1,
            max: 20,
            hint: 'properties.fieldHints.officeCapacity'
          },
          {
            name: 'hasWindows',
            label: 'properties.fields.hasWindows',
            type: 'checkbox',
            default: true
          },
          {
            name: 'furnitureIncluded',
            label: 'properties.fields.furnitureIncluded',
            type: 'checkbox',
            default: false
          },
          {
            name: 'networkPoints',
            label: 'properties.fields.networkPoints',
            type: 'number',
            min: 0,
            max: 20
          },
          {
            name: 'phoneLines',
            label: 'properties.fields.phoneLines',
            type: 'number',
            min: 0
          },
          {
            name: 'hasWhiteboard',
            label: 'properties.fields.whiteboard',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasProjector',
            label: 'properties.fields.projectorScreen',
            type: 'checkbox',
            default: false
          },
          {
            name: 'floorNumber',
            label: 'properties.fields.floorNumber',
            type: 'number',
            min: 0
          },
          {
            name: 'notes',
            label: 'properties.fields.additionalNotes',
            type: 'textarea'
          }
        ]
      },
      {
        typeKey: 'meeting_room',
        label: 'properties.roomTypes.meeting_room.label',
        pluralLabel: 'properties.roomTypes.meeting_room.plural',
        countField: 'meetingRooms',
        icon: 'groups',
        defaultCount: 0,
        description: 'properties.roomTypes.meeting_room.description',
        formFields: [
          {
            name: 'name',
            label: 'properties.fields.roomName',
            type: 'text',
            placeholder: 'properties.fieldPlaceholders.meetingRoomName'
          },
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            required: true,
            min: 10,
            max: 500
          },
          {
            name: 'capacity',
            label: 'properties.fields.seatingCapacity',
            type: 'number',
            required: true,
            min: 2,
            max: 100
          },
          {
            name: 'tableLayout',
            label: 'properties.fields.tableLayout',
            type: 'select',
            options: [
              { value: 'boardroom', label: 'properties.fieldOptions.tableLayout.boardroom' },
              { value: 'u_shape', label: 'properties.fieldOptions.tableLayout.u_shape' },
              { value: 'theatre', label: 'properties.fieldOptions.tableLayout.theatre' },
              { value: 'classroom', label: 'properties.fieldOptions.tableLayout.classroom' },
              { value: 'cabaret', label: 'properties.fieldOptions.tableLayout.cabaret' }
            ]
          },
          {
            name: 'hasProjector',
            label: 'properties.fields.hasProjector',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasVideoConferencing',
            label: 'properties.fields.videoConferencing',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasWhiteboard',
            label: 'properties.fields.whiteboard',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasSmartboard',
            label: 'properties.fields.interactiveSmartboard',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasTeleconference',
            label: 'properties.fields.teleconferencePhone',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasNaturalLight',
            label: 'properties.fields.naturalLight',
            type: 'checkbox',
            default: false
          },
          {
            name: 'notes',
            label: 'properties.fields.additionalNotes',
            type: 'textarea'
          }
        ]
      },
      {
        typeKey: 'open_workspace',
        label: 'properties.roomTypes.open_workspace.label',
        pluralLabel: 'properties.roomTypes.open_workspace.plural',
        countField: 'openWorkspaces',
        icon: 'desk',
        defaultCount: 0,
        description: 'properties.roomTypes.open_workspace.description',
        formFields: [
          {
            name: 'name',
            label: 'properties.fields.areaName',
            type: 'text',
            placeholder: 'properties.fieldPlaceholders.workspaceAreaName'
          },
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            required: true,
            min: 20,
            max: 5000
          },
          {
            name: 'workstations',
            label: 'properties.fields.numberOfWorkstations',
            type: 'number',
            required: true,
            min: 1,
            max: 500
          },
          {
            name: 'workstationType',
            label: 'properties.fields.workstationType',
            type: 'select',
            options: [
              { value: 'fixed_desk', label: 'properties.fieldOptions.workstationType.fixed_desk' },
              { value: 'hot_desk', label: 'properties.fieldOptions.workstationType.hot_desk' },
              { value: 'bench', label: 'properties.fieldOptions.workstationType.bench' },
              { value: 'cubicle', label: 'properties.fieldOptions.workstationType.cubicle' },
              { value: 'mixed', label: 'properties.fieldOptions.workstationType.mixed' }
            ]
          },
          {
            name: 'furnitureIncluded',
            label: 'properties.fields.furnitureIncluded',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasCollaborationAreas',
            label: 'properties.fields.collaborationAreas',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasPhoneBooths',
            label: 'properties.fields.phoneBooths',
            type: 'checkbox',
            default: false
          },
          {
            name: 'floorNumber',
            label: 'properties.fields.floorNumber',
            type: 'number',
            min: 0
          },
          {
            name: 'notes',
            label: 'properties.fields.additionalNotes',
            type: 'textarea'
          }
        ]
      }
    ];
  }

  getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreCommercialFeatures();
    
    const officeSpecificFeatures: FeatureConfig[] = [
      {
        key: 'elevator',
        label: 'Elevator',
        icon: 'elevator',
        category: FeatureCategory.BUILDING_ONLY, // Always building-level
        description: 'Building elevator access',
        detailsInterface: 'BuildingSystems',
        formFields: [
          {
            name: 'numberOfElevators',
            label: 'Number of Elevators',
            type: 'number',
            required: true,
            min: 1,
            default: 1
          },
          {
            name: 'maxFloorNumber',
            label: 'Serves Floors',
            type: 'number',
            min: 1
          },
          {
            name: 'capacityKg',
            label: 'Capacity',
            type: 'number',
            suffix: 'kg',
            min: 300,
            default: 630
          },
          {
            name: 'capacityPersons',
            label: 'Person Capacity',
            type: 'number',
            min: 4,
            default: 8
          }
        ]
      },
      {
        key: 'reception_area',
        label: 'Reception Area',
        icon: 'support_agent',
        category: FeatureCategory.FLEXIBLE, // Can be building-shared or office-specific
        description: 'Dedicated reception and waiting area',
        detailsInterface: 'BuildingSystems',
        formFields: [
          {
            name: 'sqm',
            label: 'Reception Area',
            type: 'number',
            suffix: 'm²',
            min: 5
          },
          {
            name: 'hasReceptionist',
            label: 'Staffed Reception',
            type: 'checkbox',
            default: false
          },
          {
            name: 'waitingCapacity',
            label: 'Waiting Area Capacity',
            type: 'number',
            min: 0
          },
          {
            name: 'hasVisitorManagement',
            label: 'Visitor Management System',
            type: 'checkbox',
            default: false
          }
        ]
      },
      {
        key: 'server_room',
        label: 'Server Room',
        icon: 'dns',
        category: FeatureCategory.FLEXIBLE, // Can be building or office-specific
        description: 'Dedicated server or data room',
        detailsInterface: 'ITInfrastructureDetail',
        formFields: [
          {
            name: 'sqm',
            label: 'Room Size',
            type: 'number',
            suffix: 'm²',
            required: true,
            min: 5
          },
          {
            name: 'powerCapacity',
            label: 'Power Capacity',
            type: 'number',
            suffix: 'kW',
            min: 1
          },
          {
            name: 'hasCoolingSystem',
            label: 'Dedicated Cooling',
            type: 'checkbox',
            default: true
          },
          {
            name: 'coolingCapacity',
            label: 'Cooling Capacity',
            type: 'number',
            suffix: 'kW'
          },
          {
            name: 'hasRaisedFloor',
            label: 'Raised Floor',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasBackupPower',
            label: 'Backup Power (UPS)',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasFireSuppression',
            label: 'Fire Suppression',
            type: 'checkbox',
            default: false
          },
          {
            name: 'rackCount',
            label: 'Server Racks',
            type: 'number',
            min: 1
          }
        ]
      },
      {
        key: 'fiber_internet',
        label: 'Fiber Internet',
        icon: 'lan',
        category: FeatureCategory.BUILDING_ONLY, // Building infrastructure
        description: 'High-speed fiber internet connection',
        detailsInterface: 'ITInfrastructureDetail',
        formFields: [
          {
            name: 'speed',
            label: 'Connection Speed',
            type: 'number',
            suffix: 'Mbps',
            min: 100
          },
          {
            name: 'provider',
            label: 'Internet Provider',
            type: 'text'
          },
          {
            name: 'hasBackupConnection',
            label: 'Backup Connection',
            type: 'checkbox',
            default: false
          },
          {
            name: 'wifiCoverage',
            label: 'WiFi Coverage',
            type: 'checkbox',
            default: false
          },
          {
            name: 'networkInfrastructure',
            label: 'Network Infrastructure',
            type: 'select',
            options: [
              { value: 'cat5e', label: 'Cat5e' },
              { value: 'cat6', label: 'Cat6' },
              { value: 'cat6a', label: 'Cat6a' },
              { value: 'fiber', label: 'Fiber Optic' }
            ]
          }
        ]
      },
      {
        key: 'kitchen_area',
        label: 'Kitchen / Break Room',
        icon: 'kitchen',
        category: FeatureCategory.FLEXIBLE, // Can be building-shared or office-specific
        description: 'Kitchen and break area for employees',
        detailsInterface: 'BuildingSystems',
        formFields: [
          {
            name: 'sqm',
            label: 'Area Size',
            type: 'number',
            suffix: 'm²',
            min: 5
          },
          {
            name: 'hasRefrigerator',
            label: 'Refrigerator',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasMicrowave',
            label: 'Microwave',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasDishwasher',
            label: 'Dishwasher',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasCoffeeMachine',
            label: 'Coffee Machine',
            type: 'checkbox',
            default: false
          },
          {
            name: 'seatingCapacity',
            label: 'Seating Capacity',
            type: 'number',
            min: 0
          }
        ]
      }
    ];

    return [...coreFeatures, ...officeSpecificFeatures];
  }

  override getMainCharacteristics(): string[] {
    return ['totalOfficeArea', 'officeRooms', 'meetingRooms', 'workstations', 'parkingSpaces'];
  }

  override getRecommendedMinSurface(): number {
    return 30; // Minimum recommended surface for an office
  }

  override getPriceRangeMultiplier(): number {
    return 1.2; // Offices typically slightly more expensive than base
  }
}

